import React from 'react';
import { Renderer } from 'amis';
import { RendererProps } from 'amis';
import Header from './components/Header';
import ResizablePanels from './components/ResizablePanels';
import DataTable from './components/DataTable';
import AIChatPanel from './components/AIChatPanel';
import HistoryDrawer from './components/HistoryDrawer';
import { ChatMessage, RecipeData } from './types';
import './styles/index.css';

export interface AIGridPluginProps extends RendererProps {
  // 可以添加自定义props
}

interface AIGridPluginState {
  tableId: string | null;
  chatHistory: ChatMessage[];
  tableData: any[];
  changedCells: any[];
  isHistoryDrawerOpen: boolean;
  currentRecipe: RecipeData | null;
  refreshTrigger: number; // 新增：刷新表格触发器
  refreshRecipeTrigger: number; // 新增：刷新配方列表触发器
  // 新增：导出触发器
  exportExcelTrigger: number;
  exportCsvTrigger: number;
  externalMessage: {
    content: string;
    options?: any;
    timestamp: number;
  } | null;
  aiUrl: string;
  apiKey: string;
}

@Renderer({
  test: /\bagimx-ai-grid$/,
  name: 'agimx-ai-grid'
})
export default class AIGridPlugin extends React.Component<AIGridPluginProps, AIGridPluginState> {

  constructor(props: AIGridPluginProps) {
    super(props);
    
    this.state = {
      tableId: null,
      chatHistory: [],
      tableData: [],
      changedCells: [],
      isHistoryDrawerOpen: false,
      currentRecipe: null,
      refreshTrigger: 0, // 初始化刷新触发器
      refreshRecipeTrigger: 0,
      // 初始化导出触发器
      exportExcelTrigger: 0,
      exportCsvTrigger: 0,
      // 新增：外部消息状态
      externalMessage: null as {
        content: string;
        options?: any;
        timestamp: number;
      } | null,
      aiUrl: this.props.aiUrl || '',
      apiKey: this.props.apiKey || '',
    };

    // 绑定所有方法
    this._handleNewRecipe = this._handleNewRecipe.bind(this);
    this._handleViewHistory = this._handleViewHistory.bind(this);
    this._handleTableIdChange = this._handleTableIdChange.bind(this);
    this._handleTableCellsChange = this._handleTableCellsChange.bind(this);
    this._handleChatHistoryChange = this._handleChatHistoryChange.bind(this);
    this._handleTableDataUpdate = this._handleTableDataUpdate.bind(this);
    this._handleHistoryDrawerClose = this._handleHistoryDrawerClose.bind(this);
    this._handleSelectRecipe = this._handleSelectRecipe.bind(this);
    this._handleDataRefresh = this._handleDataRefresh.bind(this); // 简化的刷新方法
    this._handleExportExcel = this._handleExportExcel.bind(this);
    this._handleExportCsv = this._handleExportCsv.bind(this);
  }

  componentDidMount() {
    // 组件挂载时的初始化逻辑
    console.log('AIGridPlugin mountedp-----',this.props);
  }

  componentWillUnmount() {
    // 清理逻辑
    console.log('AIGridPlugin unmounting');
  }

  _handleNewRecipe() {
    this.setState({
      tableId: null,
      chatHistory: [],
      tableData: [],
      currentRecipe: null,
    });
  }

  _handleViewHistory() {
    this.setState({
      isHistoryDrawerOpen: true,
      refreshRecipeTrigger: Date.now() // 打开历史记录时触发刷新
    });
    console.log("触发刷新配方列表");
    
  }

  _handleTableIdChange(newTableId: string) {
    this.setState({
      tableId: newTableId
    });
  }

  _handleTableCellsChange(newTableId: string, changedCells: any[]) {
    // 更新表格数据
    this.setState({ 
      tableId: newTableId,
      changedCells: changedCells 
    });
  }

  _handleChatHistoryChange(newChatHistory: ChatMessage[]) {
    this.setState({
      chatHistory: newChatHistory
    });
  }

  _handleTableDataUpdate(data: any[]) {
    this.setState({
      tableData: data,
    });
  }

  _handleHistoryDrawerClose() {
    this.setState({
      isHistoryDrawerOpen: false,
    });
  }

  _handleSelectRecipe(recipe: RecipeData) {
    this.setState({
      currentRecipe: recipe,
      // tableId: recipe.id,
      tableId: recipe.tableId,
      chatHistory: recipe.chatHistory || [],
      isHistoryDrawerOpen: false,
    });
  }

  // 简化：通过更新时间戳触发数据刷新
  async _handleDataRefresh(): Promise<void> {
    console.log('触发数据刷新');
    
    // 简单更新时间戳，useTableData会自动监听并刷新
    this.setState({
      refreshTrigger: Date.now()
    });
  }

  _handleExportExcel(){
    this.setState({
      exportExcelTrigger: Date.now() // 使用时间戳确保每次都不同
    });
  };

  _handleExportCsv(){
    this.setState({
      exportCsvTrigger: Date.now() // 使用时间戳确保每次都不同
    });
  };

  // 简化的AI问题处理
  _handleAIQuestion = async (question: string, cellContext?: any): Promise<void> => {
    try {
      const rowId = cellContext?.rowId;
      const field = cellContext?.fieldName;

      let fullQuestion = question;
      if (cellContext) {
        const contextInfo = `[当前查看：${cellContext.columnName} - ${cellContext.cellValue}] `;
        fullQuestion = contextInfo + question;
      }

      // 通过状态更新触发AIChatPanel
      this.setState({
        externalMessage: {
          content: fullQuestion,
          options: { rowId, field, cellContext },
          timestamp: Date.now()
        }
      });

      // 清除触发器
      setTimeout(() => {
        this.setState({ externalMessage: null });
      }, 100);

    } catch (error) {
      console.error('处理AI问题失败:', error);
      throw error;
    }
  };

  render() {
    const {
      tableId,
      chatHistory,
      tableData,
      changedCells,
      isHistoryDrawerOpen,
      currentRecipe,
      refreshTrigger,
      refreshRecipeTrigger,
      exportExcelTrigger,
      exportCsvTrigger,
      externalMessage
    } = this.state;

    return (
      <div className="single-grid-container" style={{height: '90vh'}}>
        <Header 
          onNewRecipe={this._handleNewRecipe}
          onViewHistory={this._handleViewHistory}
        />
        <div className="single-grid-content">
          <ResizablePanels
            leftPanel={
              <DataTable 
                tableId={tableId}
                data={tableData}
                changedCells={changedCells}
                refreshTrigger={refreshTrigger} // 传递刷新触发器
                onDataUpdate={this._handleTableDataUpdate}
                // 传递导出触发器
                exportExcelTrigger={exportExcelTrigger}
                exportCsvTrigger={exportCsvTrigger}
                // 新增：传递AI问题处理回调
                onAIQuestion={this._handleAIQuestion}
              />
            }
            rightPanel={
              <AIChatPanel 
                onTableIdChange={this._handleTableIdChange}
                onTableCellsChange={this._handleTableCellsChange} // 新增：传递表格数据更新方法
                onChatHistoryChange={this._handleChatHistoryChange}
                onDataRefresh={this._handleDataRefresh}
                chatHistory={chatHistory}
                currentRecipe={currentRecipe}
                tableId={tableId} // 修复：传递tableId给AIChatPanel
                externalMessage={externalMessage}
                aiUrl={this.state.aiUrl}
                apiKey={this.state.apiKey}
              />
            }
            leftPanelExportActions={{
              onExportExcel: this._handleExportExcel,
              onExportCsv: this._handleExportCsv,
              hasData: tableData.length > 0, // 传递数据状态
              dataCount: tableData.length,   // 传递数据条数
            }}
          />
        </div>
        <HistoryDrawer 
          isOpen={isHistoryDrawerOpen}
          onClose={this._handleHistoryDrawerClose}
          onSelectRecipe={this._handleSelectRecipe}
          refreshRecipeTrigger={refreshRecipeTrigger}
        />
      </div>
    );
  }
}